/**
 * LearningTipPopup — blocking, button-only-dismiss modal that forces a student to
 * read a question-type learning tip after a struggled question (US-001, FR5/FR6/FR8).
 *
 * Composes the DemoLimitPopup overlay/focus-trap (DemoLimitPopup.tsx L88–177) and the
 * CelebrationPopup timer idiom (CelebrationPopup.tsx L116–129), but DELIBERATELY OMITS:
 *   - Escape dismiss (DemoLimitPopup L119–121) — DD-004 / FR6 / AC-3
 *   - Backdrop onClick dismiss (CelebrationPopup L189) — DD-004 / FR6 / AC-3
 *
 * The "I understand" button is the SOLE exit (AC-3). It is disabled until the countdown
 * reaches 0 (AC-2). tipText is rendered as plain React text — no dangerouslySetInnerHTML
 * (XSS guard, security_requirements.data_validation).
 *
 * US-003 additions (AC-4, AC-5):
 *   - AC-4: dynamic aria-label on button ("Wait N second(s)" / "I understand") so AT users
 *     hear a clear countdown instead of the ambiguous raw number "(4)". aria-live removed
 *     (recommended quiet option — label alone satisfies AC-4 without per-second chatter).
 *   - AC-5: prefersReducedMotion detection (mirrors CelebrationPopup.tsx L92–113).
 *     Animated countdown progress bar substituted with static bar under reduced motion.
 *     In-component <style> @media guard (mirrors CelebrationPopup.tsx L165–184) as
 *     belt-and-braces layer 2. motion-reduce:transition-none on button = layer 3.
 *
 * Reuse evidence:
 *   - Overlay + role/aria + tabIndex: DemoLimitPopup.tsx L139–144
 *   - Card layout + button class: DemoLimitPopup.tsx L147–173
 *   - Auto-focus on open: DemoLimitPopup.tsx L98–106
 *   - Tab focus-trap: DemoLimitPopup.tsx L123–126
 *   - Timer ref + clearInterval cleanup: CelebrationPopup.tsx L91, L116–129
 *   - return null guard: DemoLimitPopup.tsx L132, CelebrationPopup.tsx L157
 *   - prefersReducedMotion state+effect: CelebrationPopup.tsx L92–113 (verbatim)
 *   - In-component <style> + @media guard: CelebrationPopup.tsx L165–184 (idiom)
 */

import { useEffect, useRef, useState, useCallback } from 'react';

export interface LearningTipPopupProps {
  /** Controls visibility. When false the component renders null. */
  isVisible: boolean;
  /** The tip text to display (Message.learningTip). Rendered as plain React text — no XSS risk. */
  tipText: string;
  /** Seconds the "I understand" button stays disabled. From config; caller passes the resolved value. */
  countdownSeconds: number;
  /** Called ONLY when the enabled "I understand" button is clicked. Advances the popup queue. */
  onDismiss: () => void;
}

/**
 * Blocking modal — US-001 / AC-1 AC-2 AC-3 FR5 FR6 FR8.
 * US-003 / AC-4 AC-5: accessible countdown label + reduced-motion guard.
 * Presentational only: no config fetch, no dedup. Parent owns those.
 */
export function LearningTipPopup({
  isVisible,
  tipText,
  countdownSeconds,
  onDismiss,
}: LearningTipPopupProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  // intervalRef mirrors CelebrationPopup.tsx L91 timerRef pattern
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [remaining, setRemaining] = useState(countdownSeconds);

  // US-003 / AC-5: Detect prefers-reduced-motion — mirrors CelebrationPopup.tsx L92–113 verbatim.
  // Belt-and-braces layer 1 (JS): drives the static vs animated bar branch.
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    if (!window.matchMedia) return;
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!mediaQuery) return;
    setPrefersReducedMotion(mediaQuery.matches);
    const handleChange = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // --- Countdown effect (Section 8 / AC-2) ---
  // Mirrors CelebrationPopup.tsx L116–129 timer idiom but uses setInterval (1 Hz) instead
  // of a one-shot setTimeout, and decrements toward 0 rather than auto-dismissing.
  useEffect(() => {
    if (!isVisible) return;

    // Reset on each open so the countdown always starts fresh.
    setRemaining(countdownSeconds);

    intervalRef.current = setInterval(() => {
      setRemaining((s) => {
        if (s <= 1) {
          // Self-clear when we reach 0 — mirrors CelebrationPopup.tsx L124–127 null-after-clear.
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          return 0;
        }
        return s - 1;
      });
    }, 1000);

    // Cleanup on unmount or when isVisible/countdownSeconds changes — prevents leak.
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isVisible, countdownSeconds]);

  // --- Auto-focus on open (a11y — mirrors DemoLimitPopup.tsx L98–106) ---
  useEffect(() => {
    if (!isVisible) return;
    const t = setTimeout(() => {
      buttonRef.current?.focus();
    }, 50);
    return () => clearTimeout(t);
  }, [isVisible]);

  const isCountingDown = remaining > 0;

  // Belt-and-braces guard: the button is already disabled, but we also guard in the
  // handler so a programmatic call cannot bypass the countdown.
  const handleDismiss = useCallback(() => {
    if (isCountingDown) return;
    onDismiss();
  }, [isCountingDown, onDismiss]);

  // Don't render if not visible — mirrors DemoLimitPopup.tsx L132, CelebrationPopup.tsx L157.
  if (!isVisible) return null;

  return (
    <>
      {/* US-003 / AC-5: Reduced-motion guard — mirrors CelebrationPopup.tsx L165–184.
          Belt-and-braces layer 2 (CSS): forces animation:none on .tip-countdown-bar even
          if the animated branch were accidentally rendered. !important mirrors L181. */}
      <style>{`
        @keyframes tipCountdownShrink {
          0%   { transform: scaleX(1); }
          100% { transform: scaleX(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .tip-countdown-bar { animation: none !important; transform: none; }
        }
      `}</style>

      {/* Overlay — NO onClick (no backdrop dismiss, AC-3 / FR6).
          onKeyDown swallows Escape and traps Tab — mirrors DemoLimitPopup.tsx L139–144 structure
          but deliberately omits the Escape→handleDismiss branch (DemoLimitPopup L119–121). */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
        role="dialog"
        aria-modal="true"
        aria-label="Learning tip"
        tabIndex={-1}
        onKeyDown={(e) => {
          // FR6 / AC-3: Escape is explicitly swallowed — it must NOT dismiss the modal.
          if (e.key === 'Escape') {
            e.preventDefault();
            return;
          }
          // Focus trap: single interactive element — keep Tab on the button.
          // Mirrors DemoLimitPopup.tsx L123–126.
          if (e.key === 'Tab') {
            e.preventDefault();
            buttonRef.current?.focus();
          }
        }}
        // NO onClick here — AC-3 / FR6: backdrop click must NOT dismiss the modal.
      >
        {/* Centered card — mirrors DemoLimitPopup.tsx L147 card layout */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 text-center max-w-sm mx-4">
          {/* Lightbulb icon */}
          <div className="text-3xl md:text-4xl mb-3" aria-hidden="true">
            💡
          </div>

          <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-2">Learning Tip</h2>

          {/* Tip text — plain React text node, never dangerouslySetInnerHTML (XSS guard). */}
          <p className="text-sm md:text-base text-gray-600 leading-relaxed mb-6">{tipText}</p>

          {/* US-003 / AC-5: Countdown progress bar — real visual affordance (shrinks over
              countdownSeconds). Only shown while counting down.
              Belt-and-braces layers:
                1. JS branch (prefersReducedMotion) → renders static bar (no animation attr).
                2. CSS @media guard above → animation:none !important on .tip-countdown-bar.
              aria-hidden: AT channel for the countdown is the button's aria-label (AC-4). */}
          {isCountingDown && (
            prefersReducedMotion ? (
              // Static substitution — no motion. aria-hidden so AT reads button label only.
              <div className="h-1 w-full bg-pebbles-500 rounded mb-4" aria-hidden="true" />
            ) : (
              <div
                className="tip-countdown-bar h-1 w-full bg-pebbles-500 rounded mb-4 origin-left"
                aria-hidden="true"
                style={{
                  animation: `tipCountdownShrink ${countdownSeconds}s linear forwards`,
                }}
              />
            )
          )}

          {/* "I understand" button — sole exit (AC-3).
              disabled during countdown (AC-2).
              US-003 / AC-4: aria-label dynamically announces "Wait N second(s)" or
                "I understand" to AT, overriding the visible "(4)" which is ambiguous raw.
                aria-live removed (recommended quiet option — label alone satisfies AC-4;
                per-second polite announcements are chatty for screen-reader users).
              US-003 / AC-5: motion-reduce:transition-none (belt-and-braces layer 3)
                neutralises the transition-colors class under reduced-motion.
              Button class mirrors DemoLimitPopup.tsx L163–173 bg-pebbles-500 style. */}
          <button
            ref={buttonRef}
            type="button"
            disabled={isCountingDown}
            aria-label={
              isCountingDown
                ? `Wait ${remaining} second${remaining !== 1 ? 's' : ''}`
                : 'I understand'
            }
            onClick={(e) => {
              e.stopPropagation();
              handleDismiss();
            }}
            className="px-6 py-2 bg-pebbles-500 hover:bg-pebbles-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed motion-reduce:transition-none"
          >
            {isCountingDown ? `I understand (${remaining})` : 'I understand'}
          </button>
        </div>
      </div>
    </>
  );
}
